// frontend/src/utils/apiErrors.js
/**
 * Parse API error response for user-friendly messages.
 *
 * Accepts either axios `error` or `error.response.data`.
 * Prefers human keys (error / non_field_errors / errors / detail) and
 * ignores sanitizer leftovers such as "Request failed".
 */

const META_KEYS = new Set(['error_type', 'code', 'status_code']);
const PREFERRED_KEYS = ['error', 'non_field_errors', 'errors', 'detail', 'message'];
const GENERIC_API_ERROR = /^(request failed\.?|an error occurred\.?|request failed with status code \d+)$/i;

/**
 * @param {unknown} value
 * @returns {string|null}
 */
// Django's str(ValidationError) is "['message']". Unwrap that so a toast
// shows the sentence. A real message is returned unchanged.
const unwrapDjangoList = (text) => {
  const match = text.match(/^\[['"]([\s\S]*)['"]\]$/);
  if (!match) return text;
  return match[1].replace(/\\'/g, "'").replace(/\\"/g, '"');
};

const firstUsefulString = (value) => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const text = unwrapDjangoList(value.trim());
    if (!text || GENERIC_API_ERROR.test(text)) return null;
    return text;
  }
  if (Array.isArray(value) && value.length) return firstUsefulString(value[0]);
  return null;
};

/**
 * @param {object|string|null} data - axios error or response.data
 * @returns {string|null} - Message to show, or null if nothing useful
 */
export const parseApiValidationErrors = (data) => {
  if (data == null) return null;
  if (typeof data === 'object' && data.response) {
    data = data.response.data;
  }
  if (data == null) return null;
  if (typeof data === 'string') return firstUsefulString(data);
  if (typeof data !== 'object') return null;

  for (const key of PREFERRED_KEYS) {
    const msg = firstUsefulString(data[key]);
    if (msg) return msg;
  }

  for (const [key, val] of Object.entries(data)) {
    if (META_KEYS.has(key) || PREFERRED_KEYS.includes(key)) continue;
    const msg = firstUsefulString(val);
    if (msg) return msg;
  }
  return null;
};
